'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  children: React.ReactElement;
  collapsedContent?: React.ReactElement;
  headerSelector?: string;
  headerSlotSelector?: string;
  className?: string;
};

export function SearchBarPortal({
  children,
  collapsedContent,
  headerSlotSelector = '#site-header-affix',
}: Props) {
  const [headerH, setHeaderH] = useState(0);
  const [stuck, setStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [inlineH, setInlineH] = useState(0);
  const inlineRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    const spacingHeader = getComputedStyle(root).getPropertyValue('--spacing-header');
    const value = parseFloat(spacingHeader) || 0;
    setHeaderH(value);
  }, []);

  // Measure inline wrapper height to preserve space when portaled
  useEffect(() => {
    const el = inlineRef.current;
    if (!el) return;
    if (el) {
      setInlineH(el.offsetHeight);
    }
  }, [children]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        setStuck(entry?.intersectionRatio === 0);
      },
      { root: null, threshold: [0], rootMargin: `-${headerH}px 0px 0px 0px` },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [headerH]);

  const slot = typeof document !== 'undefined' ? document.querySelector(headerSlotSelector) : null;

  const inline = <div ref={inlineRef}>{children}</div>;

  const portaled =
    slot && stuck
      ? createPortal(
          <div data-affixed="true" className="pointer-events-auto">
            <AppearScale affixed scaleFrom={1.5}>
              {collapsedContent}
            </AppearScale>
          </div>,
          slot,
        )
      : null;

  return (
    <>
      {/* Sentinel placed just above the inline content to detect crossing */}
      <div ref={sentinelRef} aria-hidden className="h-0 -mb-8" />
      {/* Preserve space when portaled to avoid content jump */}
      {stuck && slot ? (
        <div style={{ height: inlineH }} aria-hidden />
      ) : (
        <AppearScale scaleFrom={0.5}>{inline}</AppearScale>
      )}
      {portaled}
    </>
  );
}

// Animations use transform-based scale instead of manual pixel widths

function AppearScale({
  children,
  affixed = false,
  scaleFrom,
}: {
  children: React.ReactNode;
  affixed?: boolean;
  scaleFrom?: number;
}) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const initScale = typeof scaleFrom === 'number' ? scaleFrom : affixed ? 0.96 : 0.77;
  // const initTranslate = affixed ? 'translateY(6px)' : 'translateY(0)';
  const origin = 'center';

  return (
    <div
      style={{
        transition:
          'opacity 220ms cubic-bezier(0.22, 1, 0.36, 1), transform 500ms cubic-bezier(0.22, 1, 0.36, 1)',
        opacity: on ? 1 : 0,
        transform: on ? 'scale(1)' : `scale(${initScale})`,
        transformOrigin: origin,
      }}
    >
      {children}
    </div>
  );
}
